import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef AddReviewResponse
 * @type {Object}
 * @property {string} assetId
 * @property {{version: string}} assetMetadata
 * @property {string} assetType
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} id
 * @property {string} lastEditedAt
 * @property {number} rating
 * @property {number} replyCount
 * @property {string} title
 * @property {string} text
 */

/**
 * Adds a review for an application
 * @param {object} params - an object of params
 * @param {string} params.accessToken - IMS access token
 * @param {string} params.assetId - ID of the reviewed plugin
 * @param {string} params.assetVersion - Version of the reviewed plugin
 * @param {string} params.authorGuid - GUID of the user reviewing the plugin
 * @param {string} params.authorName - Name of the user reviewing the plugin
 * @param {number} params.rating - Rating value for the plugin
 * @param {string} [params.title] - Title of the review
 * @param {string} [params.text] - Text of the review
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<AddReviewResponse>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['accessToken', 'assetId', 'assetVersion', 'authorGuid', 'authorName', 'rating'];
  const paramsRules = {
    accessToken: { allowedTypes: ['string'] },
    assetId: { allowedTypes: ['string'] },
    assetVersion: { allowedTypes: ['string'] },
    authorGuid: { allowedTypes: ['string'] },
    authorName: { allowedTypes: ['string'] },
    rating: { allowedTypes: ['number'] },
    title: { allowedTypes: ['string'] },
    text: { allowedTypes: ['string'] },
  };

  if (params.text) {
    requiredParams.push('title', 'text');
  }

  if (!reviewSettings.endpoint['post-review']) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR add-review endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { accessToken, assetId, assetVersion, authorGuid, authorName, rating, title, text } = params;
  const response = await fetch(reviewSettings.endpoint['post-review'], {
    signal,
    method: 'POST',
    headers: {
      accept: 'application/vnd.adobe-review.review-data-v1+json',
      authorization: accessToken,
      'Content-Type': 'application/vnd.adobe-review.review-request-v1+json',
      'x-api-key': reviewSettings.apiKey,
    },
    body: JSON.stringify({
      assetId,
      assetType: 'PLUGIN',
      assetMetadata: {
        version: assetVersion,
      },
      authorGuid,
      authorName,
      rating,
      title: title?.length === 0 ? undefined : title,
      text: text?.length === 0 ? undefined : text,
    }),
  });
  /**
   * @type {AddReviewResponse}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
